/**
 * Authentication Utility Functions
 *
 * Provides helper functions for JWT token handling and LocalStorage operations
 * used in the authentication flow.
 */

import { jwtDecode } from 'jwt-decode'
import type { JWTPayload } from '~/types/auth'

// LocalStorage keys for authentication
const TOKEN_EXPIRES_AT_KEY = 'token_expires_at'
const LAST_REFRESH_KEY = 'last_refresh'
const REFRESHING_KEY = 'refreshing'
const LOGOUT_EVENT_KEY = 'logout_event'

// Refresh flag stale timeout in milliseconds (30 seconds)
// If a refresh flag is older than this, it's considered stale (e.g., crashed/closed tab)
const REFRESH_FLAG_STALE_TIMEOUT_MS = 30 * 1000

/**
 * Decode a JWT access token
 *
 * @param token - The JWT access token string
 * @returns The decoded JWT payload or null if decoding fails
 */
export function decodeAccessToken(token: string): JWTPayload | null {
  try {
    return jwtDecode<JWTPayload>(token)
  } catch (error) {
    console.error('Failed to decode JWT token:', error)
    return null
  }
}

/**
 * Save token expiration time to LocalStorage
 *
 * Stores both the expiration timestamp and the current time as last refresh.
 * Note: The expiration time should be in milliseconds (e.g., JWT exp * 1000).
 *
 * @param expiresAt - Unix timestamp in milliseconds when the token expires
 */
export function saveTokenExpiration(expiresAt: number): void {
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAt.toString())
  localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString())
}

/**
 * Get token expiration time from LocalStorage
 *
 * @returns The expiration timestamp in milliseconds as a number, or null if not set or invalid
 */
export function getTokenExpiration(): number | null {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY)
  
  if (!expiresAt) {
    return null
  }
  
  const parsed = parseInt(expiresAt, 10)
  
  if (Number.isNaN(parsed)) {
    return null
  }
  
  return parsed
}

/**
 * Clear all authentication-related data from LocalStorage
 *
 * Removes token expiration, last refresh time, refreshing flag, and logout event.
 */
export function clearAuthStorage(): void {
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
  localStorage.removeItem(LAST_REFRESH_KEY)
  localStorage.removeItem(REFRESHING_KEY)
  localStorage.removeItem(LOGOUT_EVENT_KEY)
}

/**
 * Broadcast a logout event to other tabs via localStorage
 *
 * This function writes a timestamp to the logout_event key in localStorage.
 * Other tabs listening for storage events will detect this and clear their state.
 */
export function broadcastLogoutEvent(): void {
  localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString())
}

/**
 * Set the refreshing flag in localStorage
 * Used for cross-tab synchronization to prevent multiple refresh attempts
 *
 * @param isRefreshing - Whether a refresh is in progress
 */
export function setRefreshingFlag(isRefreshing: boolean): void {
  if (isRefreshing) {
    localStorage.setItem(REFRESHING_KEY, Date.now().toString())
  } else {
    localStorage.removeItem(REFRESHING_KEY)
  }
}

/**
 * Get the refreshing flag from localStorage
 * Returns true if a refresh is in progress (within the last 30 seconds)
 * Old flags are considered stale to handle crashed/closed tabs
 *
 * @returns Whether a refresh is currently in progress
 */
export function getRefreshingFlag(): boolean {
  const refreshing = localStorage.getItem(REFRESHING_KEY)
  if (!refreshing) {
    return false
  }

  const refreshStartTime = parseInt(refreshing, 10)
  if (Number.isNaN(refreshStartTime)) {
    return false
  }

  // Consider stale if older than the timeout
  const isStale = Date.now() - refreshStartTime > REFRESH_FLAG_STALE_TIMEOUT_MS

  if (isStale) {
    // Clean up stale flag
    localStorage.removeItem(REFRESHING_KEY)
    return false
  }

  return true
}

/**
 * Callback type for auth storage sync handler
 */
export type AuthStorageSyncCallback = {
  onTokenExpiresAtChange: (newValue: number | null, oldValue: number | null) => void
  onRefreshingChange: (isRefreshing: boolean) => void
  onLogoutEvent?: () => void
}

/**
 * Create a storage event handler for cross-tab authentication synchronization
 *
 * This handler listens for localStorage changes from other tabs and calls
 * the appropriate callbacks when token_expires_at, refreshing, or logout_event values change.
 *
 * Note: The 'storage' event only fires when localStorage is changed by ANOTHER
 * window/tab, not the current one.
 *
 * @param callbacks - Object containing callback functions for each storage key
 * @returns A storage event handler function to be used with addEventListener
 */
export function createAuthStorageSyncHandler(
  callbacks: AuthStorageSyncCallback,
): (event: StorageEvent) => void {
  return (event: StorageEvent) => {
    // Handle logout event from another tab
    if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
      callbacks.onLogoutEvent?.()
      return
    }

    // Handle token_expires_at changes
    if (event.key === TOKEN_EXPIRES_AT_KEY) {
      // Parse old value for comparison
      let oldValue: number | null = null
      if (event.oldValue) {
        const parsedOld = parseInt(event.oldValue, 10)
        if (!Number.isNaN(parsedOld)) {
          oldValue = parsedOld
        }
      }

      if (event.newValue) {
        const parsed = parseInt(event.newValue, 10)
        if (!Number.isNaN(parsed)) {
          callbacks.onTokenExpiresAtChange(parsed, oldValue)
        }
      } else {
        // Token was removed (logout or cleared)
        callbacks.onTokenExpiresAtChange(null, oldValue)
      }
    }

    // Handle refreshing flag changes
    if (event.key === REFRESHING_KEY) {
      if (event.newValue) {
        // Another tab started refreshing - validate staleness
        const refreshStartTime = parseInt(event.newValue, 10)
        if (!Number.isNaN(refreshStartTime)) {
          const isStale = Date.now() - refreshStartTime > REFRESH_FLAG_STALE_TIMEOUT_MS
          // Only set refreshing to true if the flag is not stale
          callbacks.onRefreshingChange(!isStale)
        }
      } else {
        // Refresh completed in another tab
        callbacks.onRefreshingChange(false)
      }
    }
  }
}
